import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('Heic');

/**
 * HEIC/HEIF is what an iPhone actually stores, and it reaches the desktop
 * browser intact via AirDrop, iCloud Photos or a cable. Safari decodes it
 * natively; Chrome, Firefox and Edge do not, so an `<img>` (the crop preview
 * and the whole canvas pipeline) fails on it. Those files used to be rejected
 * outright, which hit exactly the users who own an iPhone and a non-Apple
 * desktop.
 *
 * `image/heic-sequence` / `image/heif-sequence` are Live Photos — the decoder
 * returns their first frame, which is the still the user meant to pick.
 */
const HEIC_MIME_TYPES = new Set([
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]);

const HEIC_EXTENSIONS = ['.heic', '.heif'];

export function isHeicFile(file: File): boolean {
  const type = file.type.toLowerCase();
  if (HEIC_MIME_TYPES.has(type)) return true;

  // Windows Chrome hands over an empty `type` for .heic — the extension is the
  // only signal left.
  if (type) return false;
  const name = file.name.toLowerCase();
  return HEIC_EXTENSIONS.some((ext) => name.endsWith(ext));
}

/**
 * Can this browser decode the file on its own? Safari can (system decoder), so
 * it must not pay for the 1.5 MB wasm decoder; every other engine cannot.
 * Probing beats sniffing the user agent: it answers for the actual file.
 */
async function canDecodeNatively(file: File): Promise<boolean> {
  if (typeof createImageBitmap !== 'function') return false;
  try {
    const bitmap = await createImageBitmap(file);
    bitmap.close();
    return true;
  } catch {
    return false;
  }
}

/**
 * Return a file every browser can draw: the original when the engine decodes
 * HEIC itself, a JPEG transcode otherwise. Non-HEIC files pass through
 * untouched, so this is safe to call on any pick.
 *
 * The decoder is imported lazily — it is only ever downloaded by a user who
 * actually picked a HEIC file.
 */
export async function normalizeHeicFile(file: File): Promise<File> {
  if (!isHeicFile(file)) return file;
  if (await canDecodeNatively(file)) return file;

  logger.info('Transcoding HEIC to JPEG', { name: file.name, size: file.size });

  const { default: heic2any } = await import('heic2any');
  const converted = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    // Above the 0.85 the canvas pipeline re-encodes at, so this intermediate
    // step never becomes the quality ceiling.
    quality: 0.92,
  });

  const blob = Array.isArray(converted) ? converted[0] : converted;
  if (!blob) throw new Error('HEIC decoder returned no image');

  return new File([blob], file.name.replace(/\.[^/.]+$/, '') + '.jpg', {
    type: 'image/jpeg',
    lastModified: file.lastModified,
  });
}
